<template>
  <div class="mainland-investor-declaration" :class="bgColor">
    <Progress progress="96" />
    <div class="main">
      <div class="declaration-body">
        <p class="paragraph">
          <span>{{ $t("mainlandInvestorDeclaration.p1Prefix") }}</span>
          <span class="fill-blank">{{ displayName }}</span>
          <span>{{ $t("mainlandInvestorDeclaration.p1Middle") }}</span>
          <span class="fill-blank">{{ displayIdCard }}</span>
          <span>{{ $t("mainlandInvestorDeclaration.p1Suffix") }}</span>
        </p>

        <van-checkbox-group v-model="checkedList" class="check-list">
          <div
            v-for="item in statementList"
            :key="item.key"
            class="check-item"
          >
            <van-checkbox
              class="check-row"
              shape="square"
              :name="item.key"
            >
              <template #icon="props">
                <img class="img-icon" :src="props.checked ? Checkd : CheckNone" />
              </template>
              <span class="check-title">{{ item.title }}</span>
            </van-checkbox>
            <p class="check-content">{{ item.content }}</p>
          </div>
        </van-checkbox-group>

        <p class="paragraph closing">{{ $t("mainlandInvestorDeclaration.closing1") }}</p>
        <p class="paragraph closing">{{ $t("mainlandInvestorDeclaration.closing2") }}</p>
      </div>
    </div>
    <nav-buttons
      custom-class="mainland-nav-buttons"
      navigationType="back"
      :disabled="!canSubmit"
      :next-text="$t('mainlandInvestorDeclaration.confirm')"
      @on-next="handleNext"
    />
  </div>
</template>

<script>
import { Checkbox, CheckboxGroup, Toast } from "vant";
import Progress from "../components/Progress.vue";
import navButtons from "../components/navButtons.vue";
import CheckNone from "@/assets/images/open-account/check_none.png";
import Checkd from "@/assets/images/open-account/check.png";

export default {
  name: "MainlandInvestorDeclaration",
  components: {
    Progress,
    navButtons,
    [Checkbox.name]: Checkbox,
    [CheckboxGroup.name]: CheckboxGroup
  },
  data() {
    return {
      CheckNone,
      Checkd,
      process_id: 1,
      realName: "",
      idCard: "",
      checkedList: []
    };
  },
  computed: {
    bgColor() {
      return this.$store.getters["common/bgColor"];
    },
    displayName() {
      return this.realName || "--";
    },
    displayIdCard() {
      return this.idCard || "--";
    },
    statementList() {
      return this.$t("mainlandInvestorDeclaration.statements") || [];
    },
    canSubmit() {
      return (
        Array.isArray(this.statementList) &&
        this.checkedList.length >= this.statementList.length
      );
    }
  },
  created() {
    this.loadCachedData();
  },
  methods: {
    /** 按声明顺序生成 0/1 数组 */
    buildConfigData() {
      return this.statementList.map(item =>
        this.checkedList.includes(item.key) ? 1 : 0
      );
    },
    /** 用 0/1 数组回填勾选 */
    restoreCheckedList(saved) {
      if (!Array.isArray(saved) || !saved.length) {
        return;
      }
      this.checkedList = this.statementList
        .filter((item, index) => saved[index] == 1)
        .map(item => item.key);
    },
    async loadCachedData() {
      try {
        const res = await this.$apis.accountSituationV2({
          step_code: "1,3-2,13-3"
        });
        this.process_id =
          res?.process_info?.["1"]?.process_id ||
          res?.get_open_account_info?.open_status ||
          1;

        const identityConfig = res?.process_info?.["3-2"]?.config_data;
        if (identityConfig) {
          const parsed =
            typeof identityConfig === "string"
              ? JSON.parse(identityConfig)
              : identityConfig;
          this.realName = parsed.real_name || parsed.english_name || "";
          this.idCard = parsed.id_card || "";
        }

        const stepConfig = res?.process_info?.["13-3"]?.config_data;
        if (stepConfig) {
          const saved =
            typeof stepConfig === "string" ? JSON.parse(stepConfig) : stepConfig;
          this.restoreCheckedList(saved);
        }
      } catch (error) {
        console.log("[mainlandInvestorDeclaration] 获取缓存失败:", error);
      }
    },
    async reportData() {
      await this.$apis.supplementSaveApi({
        process_id: this.process_id,
        step_code: "13-3",
        step_order: 13,
        config_data: this.buildConfigData()
      });
    },
    async handleNext() {
      if (!this.canSubmit) {
        return;
      }
      const loading = Toast.loading({
        duration: 0,
        forbidClick: true,
        loadingType: "spinner",
        message: this.$t("common.loading_text")
      });
      try {
        await this.reportData();
        await this.$router.push({
          path: "/open-account/customerConfirmation",
          query: {
            ...this.$route.query
          }
        });
      } finally {
        loading.clear();
      }
    }
  }
};
</script>

<style scoped lang="less">
.mainland-investor-declaration {
  min-height: 100vh;
  background: #fff;
  padding: 62px 0 200px;
  box-sizing: border-box;

  .main {
    padding: 30px;
    margin-bottom: 40px;
  }

  .declaration-body {
    margin-top: 0;
  }

  .paragraph {
    font-size: 28px;
    font-weight: 400;
    color: #17161c;
    line-height: 1.5;
    word-break: break-word;

    &.closing {
      margin-top: 36px;
    }
  }

  .fill-blank {
    display: inline;
    margin: 0 8px;
    padding: 0 4px 2px;
    border-bottom: 1px solid #17161c;
    font-weight: 500;
    white-space: nowrap;
  }

  .check-list {
    margin-top: 40px;
  }

  .check-item {
    & + .check-item {
      margin-top: 44px;
    }
  }

  .check-row {
    align-items: center;
  }

  .img-icon {
    width: 28px;
    height: 28px;
    display: block;
  }

  .check-title {
    font-size: 32px;
    font-weight: 500;
    color: #17161c;
    line-height: 44px;
  }

  .check-content {
    margin-top: 12px;
    padding-left: 44px;
    font-size: 28px;
    font-weight: 400;
    color: #17161c;
    line-height: 1.5;
  }

  /deep/ .van-checkbox__icon {
    height: auto;
  }

  /deep/ .van-checkbox__label {
    margin-left: 16px;
    line-height: 44px;
  }

  /* 盖过 navButtons scoped：允许换行，只调行高，不改宽度 */
  /deep/ .mainland-nav-buttons .next-button {
    height: auto !important;
    min-height: 104px;
    line-height: 36px !important;
    display: flex !important;
    align-items: center;
    justify-content: center;
    padding: 16px 24px;
    box-sizing: border-box;
    white-space: normal;
    word-break: keep-all;
    text-align: center;
  }
}

.black-theme.mainland-investor-declaration {
  background: #1b1c24;

  .paragraph,
  .check-title,
  .check-content {
    color: #fff;
  }

  .fill-blank {
    border-bottom-color: #fff;
  }
}
</style>
